import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, ChevronDown, ChevronRight, TrendingDown, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { usePriceDownData } from '@/hooks/usePriceDownData';
import { WeeklyPriceComparison, ProductPriceComparison } from '@/services/priceDownService';
import { getCategoryDisplayName, ProductCategory } from '@/constants/productCategories';

interface PriceDownTableProps {
  className?: string;
}

export const PriceDownTable: React.FC<PriceDownTableProps> = ({ className }) => {
  const { weeklyComparisons, isLoading, dateRange, updateDateRange } = usePriceDownData();
  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Auto-expand current week
  React.useEffect(() => {
    if (weeklyComparisons.length > 0) {
      const currentWeek = weeklyComparisons.find(week => week.isCurrentWeek);
      if (currentWeek) {
        setExpandedWeeks(new Set([currentWeek.week]));
      }
    }
  }, [weeklyComparisons]);

  const toggleWeek = (weekKey: string) => {
    const newExpanded = new Set(expandedWeeks);
    if (newExpanded.has(weekKey)) {
      newExpanded.delete(weekKey);
    } else {
      newExpanded.add(weekKey);
    }
    setExpandedWeeks(newExpanded);
  };

  const toggleCategory = (categoryKey: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryKey)) {
      newExpanded.delete(categoryKey);
    } else {
      newExpanded.add(categoryKey);
    }
    setExpandedCategories(newExpanded);
  };

  const formatPrice = (price: number) => {
    if (price === 0) return '-';
    return `${price.toFixed(2)} ETB`;
  };

  const formatPercentage = (percentage: number) => {
    if (percentage === 0) return '-';
    const sign = percentage > 0 ? '+' : '';
    return `${sign}${percentage.toFixed(1)}%`;
  };

  const getPriceChangeColor = (percentage: number) => {
    if (percentage === 0) return 'text-muted-foreground';
    return percentage > 0 ? 'text-destructive' : 'text-primary';
  };

  const getPriceChangeIcon = (percentage: number) => {
    if (percentage === 0) return null;
    return percentage > 0 ? 
      <TrendingUp className="w-4 h-4 text-destructive" /> : 
      <TrendingDown className="w-4 h-4 text-primary" />;
  };

  const renderProductRow = (product: ProductPriceComparison) => {
    const renderPriceCell = (price: number, difference: number) => {
      if (price === 0) {
        return <div className="text-center text-muted-foreground">N/A</div>;
      }
      
      const PriceIcon = getPriceChangeIcon(difference);
      
      return (
        <div className="text-center">
          <div className="text-sm">{formatPrice(price)}</div>
          <div className={cn("flex items-center justify-center gap-1 text-xs", getPriceChangeColor(difference))}>
            {PriceIcon}
            {formatPercentage(difference)}
          </div>
        </div>
      );
    };

    return (
      <tr key={product.product_name} className="border-b border-border/50">
        <td className="p-3">
          <div className="grid grid-cols-7 gap-4 text-sm">
            <div className="font-medium">{product.product_name}</div>
            <div className="text-left">{formatPrice(product.chipchip_price)}</div>
            <div>{renderPriceCell(product.competitor_prices.local_shops, product.price_differences.local_shops)}</div>
            <div>{renderPriceCell(product.competitor_prices.sunday_markets, product.price_differences.sunday_markets)}</div>
            <div>{renderPriceCell(product.competitor_prices.supermarkets, product.price_differences.supermarkets)}</div>
            <div>{renderPriceCell(product.competitor_prices.ecommerce, product.price_differences.ecommerce)}</div>
            <div className="font-medium">{renderPriceCell(product.competitor_prices.grand_total, product.price_differences.grand_total)}</div>
          </div>
        </td>
      </tr>
    );
  };

  const renderCategorySection = (category: ProductCategory, products: ProductPriceComparison[], weekKey: string) => {
    if (products.length === 0) return null;
    
    const categoryKey = `${weekKey}-${category}`;
    const isExpanded = expandedCategories.has(categoryKey);
    
    return (
      <React.Fragment key={categoryKey}>
        <tr className="bg-muted/30">
          <td colSpan={7} className="p-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => toggleCategory(categoryKey)}
              className="flex items-center gap-2 w-full justify-start"
            >
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <span className="font-medium">{getCategoryDisplayName(category)} ({products.length} products)</span>
            </Button>
          </td>
        </tr>
        {isExpanded && products.map(renderProductRow)}
      </React.Fragment>
    );
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>ChipChip Price Down Overview</CardTitle>
          <CardDescription>Loading price comparison data...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getWeekSummary = (week: WeeklyPriceComparison) => {
    const allProducts = Object.values(week.categories).flat();
    const totalProducts = allProducts.length;
    
    if (totalProducts === 0) {
      return {
        totalProducts: 0,
        avgPriceDifference: 0,
        competitiveProducts: 0,
        categoryBreakdown: { HIGH_RUNNERS: 0, MID_RUNNERS: 0, LOW_RUNNERS: 0 }
      };
    }

    const competitiveProducts = allProducts.filter(p => p.price_differences.grand_total < 0).length;

    const avgPriceDifference = allProducts.reduce((sum, product) => sum + product.price_differences.grand_total, 0) / totalProducts;

    const categoryBreakdown = {
      HIGH_RUNNERS: week.categories.HIGH_RUNNERS?.length || 0,
      MID_RUNNERS: week.categories.MID_RUNNERS?.length || 0,
      LOW_RUNNERS: week.categories.LOW_RUNNERS?.length || 0
    };

    return { totalProducts, avgPriceDifference, competitiveProducts, categoryBreakdown };
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>ChipChip Price Down Overview</span>
          <div className="flex gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {format(dateRange.startDate, 'MMM dd')} - {format(dateRange.endDate, 'MMM dd, yyyy')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={{ from: dateRange.startDate, to: dateRange.endDate }}
                  onSelect={(range) => {
                    if (range?.from && range?.to) {
                      updateDateRange(range.from, range.to);
                    }
                  }}
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>
        </CardTitle>
        <CardDescription>
          Weekly price comparison showing ChipChip prices vs competitors across different market segments
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {weeklyComparisons.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No data available for the selected date range
            </div>
          ) : (
            <>
              {/* Fixed Header */}
              <div className="bg-muted/30 rounded-lg p-3 sticky top-0 z-10">
                <div className="grid grid-cols-7 gap-4 text-sm font-medium text-muted-foreground">
                  <div className="text-left">Product</div>
                  <div className="text-left">ChipChip Price</div>
                  <div className="text-center">Local Shops</div>
                  <div className="text-center">Sunday Markets</div>
                  <div className="text-center">Supermarkets</div>
                  <div className="text-center">Ecommerce</div>
                  <div className="text-center">Grand Total</div>
                </div>
              </div>

              {weeklyComparisons.map((week) => {
                const summary = getWeekSummary(week);
                const isExpanded = expandedWeeks.has(week.week);
                
                return (
                  <div key={week.week} className="border border-border rounded-lg overflow-hidden">
                    <Button
                      variant="ghost"
                      onClick={() => toggleWeek(week.week)}
                      className={cn(
                        "w-full justify-between p-4 h-auto",
                        week.isCurrentWeek && "bg-primary/5 border-primary/20"
                      )}
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          <span className="font-medium">{week.week}</span>
                          {week.isCurrentWeek && (
                            <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded">Current</span>
                          )}
                        </div>
                        {!isExpanded && summary.totalProducts > 0 && (
                          <div className="flex gap-4 text-sm text-muted-foreground">
                            <span>{summary.totalProducts} products</span>
                            <span className={summary.avgPriceDifference < 0 ? "text-primary" : "text-destructive"}>
                              Avg: {summary.avgPriceDifference > 0 ? '+' : ''}{summary.avgPriceDifference.toFixed(1)}%
                            </span>
                            <span>{summary.competitiveProducts}/{summary.totalProducts} competitive</span>
                          </div>
                        )}
                      </div>
                    </Button>
                    
                    {isExpanded && (
                      <div className="border-t border-border">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <tbody>
                              {renderCategorySection('HIGH_RUNNERS', week.categories.HIGH_RUNNERS, week.week)}
                              {renderCategorySection('MID_RUNNERS', week.categories.MID_RUNNERS, week.week)}
                              {renderCategorySection('LOW_RUNNERS', week.categories.LOW_RUNNERS, week.week)}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};